
import { computed, defineComponent, onMounted, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";

import {
  convertRequestErrorToMap,
  isNotNullish,
  notEmpty,
  Nullable,
  useResource,
  useToast,
} from "@tager/admin-services";
import {
  createTabErrorFinder,
  FormFieldFileInput,
  TabType,
  FormFooter,
  TagerFormSubmitEvent,
  SeoFieldGroup,
  SeoChangeEvent,
  FormFieldRichTextInput,
  FormFieldUrlAliasInput,
  FormField,
} from "@tager/admin-ui";
import { Page } from "@tager/admin-layout";

import { FullBrandType } from "@/typings/model";
import { createBrand, getBrand, updateBrand } from "@/services/requests";
import {
  convertFormValuesToBrandCreationPayload,
  convertFormValuesToBrandUpdatePayload,
  convertBrandToFormValues,
  FormValues,
} from "@/views/BrandForm/BrandForm.helpers";
import { getBrandFormUrl, getBrandListUrl } from "@/utils/paths";
import { getWebsiteOrigin } from "@/utils/common";

export default defineComponent({
  name: "BrandForm",
  components: {
    Page,
    FormField,
    FormFieldRichTextInput,
    FormFieldUrlAliasInput,
    FormFooter,
    FormFieldFileInput,
    SeoFieldGroup,
  },
  setup() {
    const route = useRoute();
    const router = useRouter();
    const toast = useToast();

    const brandId = computed<string>(() => route.params.brandId as string);

    const isCreation = computed<boolean>(() => brandId.value === "create");

    const [fetchBrand, { data: brand, loading: isBrandLoading }] = useResource<
      Nullable<FullBrandType>
    >({
      fetchResource: () => {
        if (!isCreation.value && brandId.value) {
          return getBrand(brandId.value);
        } else {
          return new Promise((resolve) =>
            resolve({
              data: null,
            })
          );
        }
      },
      initialValue: null,
      resourceName: "Производитель",
    });

    onMounted(() => {
      fetchBrand();
    });

    watch(brandId, () => {
      fetchBrand();
    });

    const values = ref<FormValues>(convertBrandToFormValues(brand.value));

    const errors = ref<Record<string, string>>({});
    const isSubmitting = ref<boolean>(false);

    watch(brand, () => {
      values.value = convertBrandToFormValues(brand.value);
    });

    function submitForm(event: TagerFormSubmitEvent) {
      isSubmitting.value = true;

      const creationBody = convertFormValuesToBrandCreationPayload(
        values.value
      );
      const updateBody = convertFormValuesToBrandUpdatePayload(values.value);
      const requestPromise = isCreation.value
        ? createBrand(creationBody)
        : updateBrand(brandId.value, updateBody);

      requestPromise
        .then((response) => {
          errors.value = {};

          if (event.type === "create") {
            router.push(getBrandFormUrl({ brandId: response.data.id }));
          }
          if (event.type === "create_exit" || event.type === "save_exit") {
            router.push(getBrandListUrl());
          }
          if (event.type === "create_create-another") {
            values.value = convertBrandToFormValues(null);
          }

          toast.show({
            variant: "success",
            title: "Success",
            body: isCreation.value
              ? `Производитель успешно добавлен`
              : "Производитель успешно обновлен",
          });
        })
        .catch((error) => {
          console.error(error);
          errors.value = convertRequestErrorToMap(error);
          toast.show({
            variant: "danger",
            title: "Ошибка",
            body: isCreation.value
              ? `Ошибка при добавлении производителя`
              : "Ошибка при обновлении производителя",
          });
        })
        .finally(() => {
          isSubmitting.value = false;
        });
    }

    const isContentLoading = computed<boolean>(() => isBrandLoading.value);

    /** Tabs */

    const tabList = computed<Array<TabType>>(() => {
      const hasErrors = createTabErrorFinder(errors.value);

      return [
        {
          id: "common",
          label: "Основная",
          hasErrors: hasErrors(["content", "urlAlias"]),
        },
        {
          id: "seo",
          label: "SEO",
          hasErrors: hasErrors([
            "pageTitle",
            "pageDescription",
            "openGraphImage",
          ]),
        },
      ].filter(notEmpty);
    });

    const selectedTabId = ref<string>(tabList.value[0].id);

    const pageTitle = computed<string>(() => {
      return isCreation.value
        ? `Добавить производителя`
        : `Редактировать производителя "${
            brand.value ? brand.value.name : "...."
          }"`;
    });

    const pagePath = computed<string>(() => {
      return getWebsiteOrigin() + "/brands/";
    });

    const headerButtonList = computed<
      Array<{ text: string; href: string; target?: string; class?: string }>
    >(() =>
      [
        brand.value
          ? {
              text: "Посмотреть на сайте",
              href: getWebsiteOrigin() + "/brands/" + brand.value.urlAlias,
              target: "_blank",
            }
          : null,
      ].filter(isNotNullish)
    );

    function handleSearchEngineOptimization({
      title,
      description,
      image,
    }: SeoChangeEvent) {
      values.value.pageTitle = title;
      values.value.pageDescription = description;
      values.value.openGraphImage = image;
    }

    return {
      backButtonUrl: getBrandListUrl(),
      values,
      errors,
      isSubmitting,
      submitForm,
      isContentLoading,
      brand,
      isCreation,
      pageTitle,
      tabList,
      selectedTabId,
      pagePath,
      headerButtonList,

      handleSearchEngineOptimization,
    };
  },
});
